import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, Target, Award, DollarSign, Users, FileText, ShoppingCart,
  Loader2, ChevronDown, BarChart3, PieChart
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area, Cell } from 'recharts';
import { salesAnalyticsAPI, branchAPI } from '../../services/api';

const FUNNEL_COLORS = {
  Lead: '#9CA3AF', Qualified: '#3a6fc4', Quoted: '#24549c',
  Negotiation: '#E65100', Won: '#2E7D32', Lost: '#C62828',
};

const SalesAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ branch: '', period: 'all' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.branch) params.branch = filters.branch;
      if (filters.period !== 'all') params.period = filters.period;
      setData(await salesAnalyticsAPI.get(params));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); branchAPI.getAll().then(setBranches).catch(() => {}); }, [fetchData]);

  if (loading && !data) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#24549c' }} />
    </div>
  );

  const s = data?.summary || {};
  const fmt = (v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v);

  return (
    <div className="space-y-5" data-testid="sales-analytics">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select data-testid="analytics-period" value={filters.period} onChange={e => setFilters(f => ({ ...f, period: e.target.value }))}
          className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
          <option value="all">All Time</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="6m">Last 6 Months</option>
          <option value="1y">Last 1 Year</option>
        </select>
        <select data-testid="analytics-branch" value={filters.branch} onChange={e => setFilters(f => ({ ...f, branch: e.target.value }))}
          className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
          <option value="">All Branches</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {/* KPI Row 1: Pipeline */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI icon={Target} label="Win Rate" value={`${s.win_rate || 0}%`} sub={`${s.active_leads || 0} active leads`} color="#2E7D32" />
        <KPI icon={TrendingUp} label="Pipeline Value" value={`₹${fmt(s.pipeline_value || 0)}`} sub={`${s.total_leads || 0} total leads`} color="#24549c" />
        <KPI icon={Award} label="Won Revenue" value={`₹${fmt(s.won_value || 0)}`} sub={`Avg deal ₹${fmt(s.avg_deal_size || 0)}`} color="#7c3aed" />
        <KPI icon={DollarSign} label="OC Revenue" value={`₹${fmt(s.total_oc_value || 0)}`} sub={`${s.total_ocs || 0} confirmations`} color="#ea580c" />
      </div>

      {/* KPI Row 2: Quotations & OCs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI icon={FileText} label="Quotations" value={s.total_quotes || 0} sub={`₹${fmt(s.total_quoted_value || 0)} quoted`} color="#3a6fc4" />
        <KPI icon={ShoppingCart} label="Quote Conversion" value={`${s.quote_conversion || 0}%`} sub={`₹${fmt(s.accepted_value || 0)} accepted`} color="#0ea5e9" />
        <KPI icon={BarChart3} label="Dispatched Value" value={`₹${fmt(s.dispatched_value || 0)}`} sub={`of ₹${fmt(s.total_oc_value || 0)} total`} color="#059669" />
        <KPI icon={PieChart} label="Avg Deal Size" value={`₹${fmt(s.avg_deal_size || 0)}`} sub="Per won deal" color="#8b5cf6" />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Pipeline Funnel */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>Pipeline Funnel</h3>
          {(data?.funnel || []).length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">No pipeline data</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.funnel} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                <YAxis dataKey="stage" type="category" tick={{ fontSize: 10, fill: '#4B5563' }} width={80} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '11px' }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {(data.funnel || []).map((entry, idx) => (
                    <Cell key={idx} fill={FUNNEL_COLORS[entry.stage] || '#9CA3AF'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Monthly Trend */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>Monthly Revenue Trend</h3>
          {(data?.monthly_trend || []).length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">No trend data</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} tickFormatter={v => v >= 100000 ? `${(v/100000).toFixed(0)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '11px' }} formatter={(v) => [`₹${v.toLocaleString()}`, 'Revenue']} />
                <Area type="monotone" dataKey="revenue" stroke="#24549c" fill="#24549c" fillOpacity={0.12} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Salesman Performance */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>Salesman Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">Name</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Leads</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Won</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Quotes</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">OCs</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">OC Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data?.salesman_performance || []).length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-gray-400">No data</td></tr>
                ) : (data?.salesman_performance || []).map((sm, i) => (
                  <tr key={i} className="hover:bg-gray-50/50" data-testid={`salesman-row-${i}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: i === 0 ? '#24549c' : i === 1 ? '#3a6fc4' : '#9CA3AF' }}>
                          {(sm.name || '?')[0]}
                        </div>
                        <span className="font-medium text-gray-900">{sm.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-600">{sm.leads}</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={`font-semibold ${sm.won > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>{sm.won}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-600">{sm.quotes}</td>
                    <td className="px-3 py-2.5 text-right text-gray-600">{sm.ocs}</td>
                    <td className="px-3 py-2.5 text-right font-semibold" style={{ color: '#24549c' }}>₹{fmt(sm.oc_value || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Customers by Revenue */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>Top Customers by Revenue</h3>
          <div className="space-y-2.5">
            {(data?.top_customers || []).length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-8">No customer revenue data</p>
            ) : (data?.top_customers || []).map((c, i) => {
              const maxRev = data.top_customers[0]?.revenue || 1;
              const pct = Math.round(c.revenue / maxRev * 100);
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`customer-row-${i}`}>
                  <span className="text-[10px] font-bold text-gray-400 w-4 text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-800 truncate">{c.name}</span>
                      <span className="text-xs font-bold ml-2 whitespace-nowrap" style={{ color: '#24549c' }}>₹{fmt(c.revenue)}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: i === 0 ? '#24549c' : i < 3 ? '#3a6fc4' : '#9CA3AF' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quotes & OC Activity */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>Monthly Activity</h3>
          {(data?.monthly_trend || []).length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">No activity data</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '11px' }} />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
                <Bar dataKey="quotes" fill="#3a6fc4" name="Quotations" radius={[2, 2, 0, 0]} />
                <Bar dataKey="ocs" fill="#24549c" name="OCs" radius={[2, 2, 0, 0]} />
                <Bar dataKey="won_deals" fill="#2E7D32" name="Won Deals" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Status Breakdown */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>Status Breakdown</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Quotations</p>
              {Object.entries(data?.quote_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between py-1.5 border-b border-gray-50">
                  <span className="text-xs text-gray-600">{status}</span>
                  <span className="text-xs font-bold text-gray-900">{count}</span>
                </div>
              ))}
              {Object.keys(data?.quote_status || {}).length === 0 && <p className="text-[10px] text-gray-400">No data</p>}
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Order Confirmations</p>
              {Object.entries(data?.oc_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between py-1.5 border-b border-gray-50">
                  <span className="text-xs text-gray-600">{status}</span>
                  <span className="text-xs font-bold text-gray-900">{count}</span>
                </div>
              ))}
              {Object.keys(data?.oc_status || {}).length === 0 && <p className="text-[10px] text-gray-400">No data</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-all">
    <div className="flex items-center justify-between mb-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}14` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
    </div>
    <p className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{value}</p>
    <p className="text-[10px] text-gray-500 mt-0.5">{label}</p>
    {sub && <p className="text-[9px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

export default SalesAnalytics;
